import React, { useCallback, useEffect, useState } from 'react';

import { createStyles, withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import GoogleMapReact, { Coords, Props as GoogleMapReactProps } from 'google-map-react';
import { head } from 'lodash';
import classnames from 'classnames';

import usePlacesService from '@lib/hooks/usePlacesService';
import configService from '@lib/services/configService';

import { LumaTheme } from '../styles/getTheme';

const key = configService.getKeyGooglMaps();

const styles = (theme: LumaTheme) =>
  createStyles({
    marker: {
      width: theme.spacing.unit * 3,
      height: theme.spacing.unit * 3,
    },
    root: {
      position: 'relative',
    },
    closeButton: {
      top: '5px',
      right: '5px',
      border: '1px solid black',
      zIndex: 1,
      position: 'absolute',
      backgroundColor: 'white',
      minWidth: '0',
      width: '20px',
      height: '20px',
    },
    closeIcon: {
      padding: '2px',
    },
  });

interface Props extends GoogleMapReactProps {
  classes: any;
  // `className` required > Always set the container height explicitly
  // https://github.com/google-map-react/google-map-react#getting-started
  className: string;
  query?: string;
  queryFallback?: string;
  defaultZoomFallback?: number;
  closeMap?: any;
}

const Map: React.FC<Props> = ({
  classes,
  className,
  query,
  queryFallback,
  defaultZoom = 15,
  defaultZoomFallback = 12,
  defaultCenter: coords,
  closeMap = false,
  ...other
}) => {
  const [service] = usePlacesService('Map');
  const [defaultCenter, setDefaultCenter] = useState<Coords | undefined>(coords);
  const [usingFallback, setUsingFallback] = useState(false);

  const handleSearch = useCallback(
    (q: string, callbackEmpty?: any) => {
      // @ts-ignore
      service.textSearch({ query: q }, (results: any[]) => {
        const item = head(results);
        if (item) {
          const result = {
            lat: item.geometry.location.lat(),
            lng: item.geometry.location.lng(),
          };
          setDefaultCenter(result);
        } else if (callbackEmpty) {
          callbackEmpty();
        }
      });
    },
    [service],
  );

  useEffect(() => {
    if (query && service) {
      handleSearch(query, () => {
        if (queryFallback) {
          setUsingFallback(true);
          handleSearch(queryFallback);
        }
      });
    }
  }, [handleSearch, query, queryFallback, service]);

  return (
    <div className={classnames(classes.root, className)}>
      {defaultCenter && key && (
        <>
          {closeMap && (
            <Button
              className={classes.closeButton}
              aria-label="close map"
              onClick={closeMap}
              color="primary"
              size="small"
            >
              <CloseIcon className={classes.closeIcon} />
            </Button>
          )}
          <GoogleMapReact
            bootstrapURLKeys={{ key }}
            defaultCenter={defaultCenter}
            defaultZoom={usingFallback ? defaultZoomFallback : defaultZoom}
            options={() => ({ fullscreenControl: !closeMap })}
            {...other}
          >
            {!usingFallback && (
              // @ts-ignore
              <img
                className={classes.marker}
                src="https://maps.gstatic.com/mapfiles/place_api/icons/geocode-71.png"
                // @ts-ignore
                lat={defaultCenter.lat}
                lng={defaultCenter.lng}
                alt={query}
              />
            )}
          </GoogleMapReact>
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(Map);
